import React, { useState, useRef, useEffect } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import logo from "../assets/logo2.png";
import SignUpModal from "./SignUpModal";

const NAV = [
  { label: "Home", href: "/" },
  { label: "Services", href: "#services" },
  { label: "Pricing", to: "/pricing" },
  { label: "Contact", href: "#contact" },
];

const MORE = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToLogin = () => {
    const el = document.getElementById("login-card");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E2E8F0]">
        <nav className="max-w-7xl mx-auto px-4 sm:px-5 h-[60px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <img src={logo} alt="Logo" className="h-8 w-8 sm:h-9 sm:w-9 object-contain" />
            <div>
              <span className="text-[15px] font-bold text-[#0F172A] leading-tight block">Fastag Tracking</span>
              <span className="text-[9px] text-[#94A3B8] font-medium tracking-wider uppercase hidden sm:block">Professional Fleet Solutions</span>
            </div>
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-0.5">
            {NAV.map((l) =>
              l.to
                ? <Link key={l.label} to={l.to} className="px-3.5 py-2 text-[13px] font-medium text-[#475569] hover:text-[#1E40AF] hover:bg-[#F8FAFC] rounded-full transition-colors">{l.label}</Link>
                : <a key={l.label} href={l.href} className="px-3.5 py-2 text-[13px] font-medium text-[#475569] hover:text-[#1E40AF] hover:bg-[#F8FAFC] rounded-full transition-colors">{l.label}</a>
            )}

            <div ref={moreRef} className="relative">
              <button
                onClick={() => setMoreOpen(v => !v)}
                className={`flex items-center gap-1 px-3.5 py-2 text-[13px] font-medium rounded-full transition-colors ${moreOpen ? 'text-[#1E40AF] bg-[#F8FAFC]' : 'text-[#475569] hover:text-[#1E40AF] hover:bg-[#F8FAFC]'}`}
              >
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl shadow-black/8 border border-[#E2E8F0] py-2 z-50">
                  {MORE.map((m) => (
                    <Link key={m.label} to={m.to} onClick={() => setMoreOpen(false)} className="block px-4 py-2.5 text-[13px] text-[#475569] hover:text-[#1E40AF] hover:bg-[#F8FAFC] transition-colors">{m.label}</Link>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 ml-4">
              <Button variant="outline" onClick={scrollToLogin} className="h-9 px-4 text-[13px] font-semibold rounded-full border-[#E2E8F0] text-[#475569] hover:text-[#1E40AF] hover:border-[#1E40AF]">Sign In</Button>
              <Button data-signup-trigger onClick={() => setSignUpOpen(true)} className="h-9 px-5 text-[13px] font-semibold bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-full">Sign Up</Button>
            </div>
          </div>

          {/* Mobile */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden"><Menu className="h-5 w-5 text-[#475569]" /></Button></SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-white p-0">
              <div className="p-4 border-b border-[#F1F5F9] flex items-center gap-2">
                <img src={logo} alt="" className="h-8 w-8" />
                <span className="font-bold text-[#0F172A] text-[14px]">Fastag Tracking</span>
              </div>
              <div className="flex flex-col p-3 gap-0.5">
                {NAV.map((l) =>
                  l.to
                    ? <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)} className="px-4 py-3 text-[14px] font-medium text-[#0F172A] hover:bg-[#F8FAFC] rounded-xl">{l.label}</Link>
                    : <a key={l.label} href={l.href} onClick={() => setMobileOpen(false)} className="px-4 py-3 text-[14px] font-medium text-[#0F172A] hover:bg-[#F8FAFC] rounded-xl">{l.label}</a>
                )}
                {MORE.map((m) => <Link key={m.label} to={m.to} onClick={() => setMobileOpen(false)} className="px-4 py-3 text-[14px] font-medium text-[#475569] hover:bg-[#F8FAFC] rounded-xl">{m.label}</Link>)}
                <div className="flex flex-col gap-2 mt-4 px-3">
                  <Button variant="outline" onClick={() => { setMobileOpen(false); scrollToLogin(); }} className="w-full rounded-full border-[#E2E8F0] text-[13px]">Sign In</Button>
                  <Button data-signup-trigger onClick={() => { setMobileOpen(false); setSignUpOpen(true); }} className="w-full rounded-full bg-[#1E40AF] text-white text-[13px]">Sign Up</Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </header>

      <SignUpModal open={signUpOpen} onOpenChange={setSignUpOpen} />
    </>
  );
};

export default Header;
